<div class="relative pb-24 lg:pb-0">
  <div class="transition" [class.blur-sm]="isLoadingData()" [class.opacity-60]="isLoadingData()"
    [class.pointer-events-none]="isLoadingData()" [attr.aria-busy]="isLoadingData()">

    <!-- ============================================================ -->
    <!-- PAYMENT RESULT BANNER                                        -->
    <!-- ============================================================ -->
    <div *ngIf="paymentResult() as result" class="mb-4 rounded-2xl p-4 flex flex-col gap-2"
      [ngClass]="paymentResultStyles().container">
      <div class="flex items-start gap-3">
        <div class="rounded-full p-2" [ngClass]="paymentResultStyles().iconBg">
          <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Z" />
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4" />
          </svg>
        </div>
        <div class="flex-1">
          <p class="text-sm font-semibold" [ngClass]="paymentResultStyles().accent">
            {{ paymentResultTitle() }}
          </p>
          <p class="text-sm text-gray-700 dark:text-gray-200">
            {{ result.message || 'Recibimos la respuesta de ePayco para tu pago.' }}
          </p>
          <div class="mt-2 grid gap-1 text-xs text-gray-600 dark:text-gray-300 sm:grid-cols-2">
            <span *ngIf="result.refPayco">Ref. ePayco:
              <span class="font-semibold">{{ result.refPayco }}</span>
            </span>
            <span *ngIf="result.transactionId">Transaccion:
              <span class="font-semibold">{{ result.transactionId }}</span>
            </span>
            <span *ngIf="result.amount">
              Valor:
              <span class="font-semibold">{{ result.amount | currency:(result.currency || 'COP') }}</span>
              <span *ngIf="result.isTest"
                class="ml-1 inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                Test
              </span>
            </span>
          </div>
        </div>
        <button type="button" class="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-300"
          (click)="clearPaymentResult()">
          <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M18 6 6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </div>

    @if (view() === 'overview') {
      <!-- ============================================================ -->
      <!-- OVERVIEW: current plan + payments                            -->
      <!-- ============================================================ -->
      @if (latestMembership(); as lm) {
        @let state = membershipState();
        @let per = isYearlyMembership() ? 'año' : 'mes';
        @let seats = seatCount() ?? lm.membershipPlan.numberOfProfessionals;
        <section class="mo-plan mb-6" [attr.data-state]="state" [attr.data-tone]="state === 'trial' ? trialTone() : null"
          aria-labelledby="mo-plan-title">
          <div class="mo-plan__main">
            <!-- Headline: what's true about the membership right now -->
            @switch (state) {
              @case ('trial') {
                <p class="mo-plan__eyebrow">Prueba gratis</p>
                <h2 id="mo-plan-title" class="mo-plan__name">Plan {{ lm.membershipPlan.name }}</h2>
                @if (trialProgress(); as t) {
                  <div class="mo-trial">
                    <div class="mo-trial__bar" aria-hidden="true"><span [style.width.%]="t.percent"></span></div>
                    <p>
                      @if (t.left > 0) {
                        Te {{ t.left === 1 ? 'queda 1 día' : 'quedan ' + t.left + ' días' }} de prueba.
                      } @else {
                        Tu prueba termina hoy.
                      }
                      Después, {{ displayPrice() | currency:'COP':'symbol-narrow':'1.0-0' }} al {{ per }}.
                    </p>
                  </div>
                }
                @if (upgradeOffer(); as up) {
                  <p class="mo-upsell">
                    Aprovecha la prueba para decidir: por {{ up.extraPerMonth | currency:'COP':'symbol-narrow':'1.0-0' }} más al mes,
                    el plan {{ up.plan.name }} te da {{ upgradeGains() }}@if (up.savings) {<span>, {{ up.savings | currency:'COP':'symbol-narrow':'1.0-0' }} menos que agregarlo por separado</span>}.
                    <button type="button" (click)="chooseUpgrade()">Ver {{ up.plan.name }}</button>
                  </p>
                }
              }
              @case ('active') {
                <p class="mo-plan__eyebrow">Activa</p>
                <h2 id="mo-plan-title" class="mo-plan__name">Plan {{ lm.membershipPlan.name }}</h2>
                <p class="mo-plan__lead">
                  Pagas <strong>{{ currentPeriodTotal() | currency:'COP':'symbol-narrow':'1.0-0' }}</strong> al {{ per }}@if (prepaidNextCharge(); as next) {<span>. Ya pagaste hasta el {{ lm.paidThroughDate | date:"d 'de' MMMM":'UTC' }}; el próximo cobro es el {{ next | date:"d 'de' MMMM":'UTC' }}.</span>} @else if (lm.nextBillingDate || lm.endDate) {<span>; el próximo cobro es el {{ (lm.nextBillingDate || lm.endDate) | date:"d 'de' MMMM":'UTC' }}.</span>} @else {<span>.</span>}
                </p>
              }
              @case ('suspended') {
                <p class="mo-plan__eyebrow">Pago pendiente</p>
                <h2 id="mo-plan-title" class="mo-plan__name">Tu membresía está suspendida</h2>
                <p class="mo-plan__lead">No pudimos cobrar tu plan {{ lm.membershipPlan.name }}. Paga para reactivarlo.</p>
              }
              @case ('pending') {
                <p class="mo-plan__eyebrow">Confirmando pago</p>
                <h2 id="mo-plan-title" class="mo-plan__name">Plan {{ lm.membershipPlan.name }}</h2>
                <p class="mo-plan__lead">Estamos esperando la confirmación de ePayco. Esto suele tardar unos minutos.</p>
              }
              @default {
                <p class="mo-plan__eyebrow">{{ state === 'cancelled' ? 'Cancelada' : 'Vencida' }}</p>
                <h2 id="mo-plan-title" class="mo-plan__name">
                  @if (lm.endDate || lm.trialEndDate) {
                    Tu membresía {{ state === 'cancelled' ? 'terminó' : 'venció' }} el {{ (lm.endDate || lm.trialEndDate) | date:"d 'de' MMMM":'UTC' }}
                  } @else {
                    Tu membresía no está activa
                  }
                </h2>
                <p class="mo-plan__lead">
                  Renuévala para seguir usando CitaLink Pro. Tu último plan fue
                  <strong>{{ lm.membershipPlan.name }}</strong>, {{ displayPrice() | currency:'COP':'symbol-narrow':'1.0-0' }} al {{ per }}.
                </p>
              }
            }

            <!-- Facts that matter in every state -->
            <dl class="mo-facts">
              <div>
                <dt>Cobro</dt>
                <dd>{{ lm.billingCycle.displayName }}</dd>
              </div>
              <div>
                <dt>Profesionales</dt>
                <dd>{{ seats }} cupo{{ seats !== 1 ? 's' : '' }}</dd>
              </div>
              @if (currentAddOns().length) {
                <div class="min-w-0">
                  <dt>Adicionales</dt>
                  <dd class="truncate">
                    @for (f of currentAddOns(); track f.businessMembershipFeatureId; let last = $last) {
                      {{ f.featureName }}@if (f.quantity > 1) {<span> ×{{ f.quantity }}</span>}@if (!last) {<span>, </span>}
                    }
                  </dd>
                </div>
              }
            </dl>
          </div>

          <!-- One clear next step per state -->
          <div class="mo-plan__actions">
            @switch (state) {
              @case ('active') {
                <!-- Early renewal pays the next period; hidden once it's prepaid so it isn't paid twice by mistake -->
                @if (!prepaidNextCharge() && !authServicePublic.isImpersonating()) {
                  <button type="button" class="mp-btn mp-btn--primary" (click)="confirmPay()" [disabled]="isProcessingPayment()">
                    {{ isProcessingPayment() ? 'Abriendo el pago…' : 'Renovar por ' + (displayPrice() | currency:'COP':'symbol-narrow':'1.0-0') }}
                  </button>
                }
                <button type="button" class="mp-btn mp-btn--ghost" (click)="openPlans()">Cambiar plan o adicionales</button>
              }
              @case ('pending') {}
              @case ('trial') {
                @if (!authServicePublic.isImpersonating()) {
                  <button type="button" class="mp-btn mp-btn--primary" (click)="confirmPay()" [disabled]="isProcessingPayment()">
                    {{ isProcessingPayment() ? 'Abriendo el pago…' : 'Contratar ' + lm.membershipPlan.name }}
                  </button>
                }
                <button type="button" class="mp-btn mp-btn--upgrade" (click)="openPlans()">
                  <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                  </svg>
                  Ver otros planes
                </button>
              }
              @default {
                @if (!authServicePublic.isImpersonating()) {
                  <button type="button" class="mp-btn mp-btn--primary" (click)="confirmPay()" [disabled]="isProcessingPayment()">
                    {{ isProcessingPayment() ? 'Abriendo el pago…' : (state === 'suspended' ? 'Pagar ' : 'Renovar por ') + (displayPrice() | currency:'COP':'symbol-narrow':'1.0-0') }}
                  </button>
                }
                <button type="button" class="mp-btn mp-btn--upgrade" (click)="openPlans()">
                  <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
                  </svg>
                  Ver otros planes
                </button>
              }
            }
            <button type="button" class="mo-link" (click)="downloadMembershipReport()" [disabled]="isDownloadingMembershipReport()">
              {{ isDownloadingMembershipReport() ? 'Descargando…' : 'Descargar reporte' }}
            </button>
          </div>
        </section>
      } @else if (!membershipLoaded()) {
        <div class="mo-plan mb-6 animate-pulse" role="status" aria-label="Cargando tu plan">
          <div class="mo-plan__main">
            <div class="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-800"></div>
            <div class="mt-3 h-7 w-56 rounded bg-gray-200 dark:bg-gray-800"></div>
            <div class="mt-3 h-4 w-72 max-w-full rounded bg-gray-100 dark:bg-gray-800/70"></div>
          </div>
        </div>
      }

      <section aria-labelledby="mo-payments-title">
        <div class="mb-3">
          <h3 id="mo-payments-title" class="text-base font-semibold text-gray-900 dark:text-white">Pagos</h3>
          <p class="mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400">Cobros de tu plan, profesionales y adicionales, del más reciente al más antiguo.</p>
        </div>

        @if (paymentHistoryError()) {
          <div class="rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-300">
            {{ paymentHistoryError() }}
          </div>
        } @else if (isPaymentHistoryLoading() && !invoices().length) {
          <div class="mo-payments animate-pulse" role="status" aria-label="Cargando pagos">
            @for (i of [1, 2, 3]; track i) {
              <div class="flex items-center gap-3 px-5 py-4">
                <div class="h-4 flex-1 rounded bg-gray-100 dark:bg-gray-800/70"></div>
                <div class="h-4 w-20 rounded bg-gray-200 dark:bg-gray-800"></div>
              </div>
            }
          </div>
        } @else if (!invoices().length) {
          <div class="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center dark:border-gray-700">
            <p class="text-sm text-gray-500 dark:text-gray-400">Tus pagos aparecerán aquí cuando contrates un plan.</p>
          </div>
        } @else {
          <ul class="mo-payments">
            @for (inv of visiblePayments(); track inv.id) {
              <ng-container *ngTemplateOutlet="paymentRow; context: { $implicit: inv }"></ng-container>
            }
          </ul>
          @if (hiddenPaymentsCount() > 0) {
            <button type="button" class="mo-more" (click)="showAllPayments.set(!showAllPayments())"
              [attr.aria-expanded]="showAllPayments()">
              {{ showAllPayments() ? 'Ver menos' : 'Ver todos los pagos (' + invoices().length + ')' }}
            </button>
          }
        }
      </section>
    } @else {
      <!-- ============================================================ -->
      <!-- CHANGE PLAN                                                  -->
      <!-- ============================================================ -->
      @if (hasCurrentPlan()) {
        <button type="button" class="mo-back" (click)="closePlans()">
          <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Volver a tu membresía
        </button>
      }
      <!-- ============================================================ -->
      <!-- PLANS                                                        -->
      <!-- ============================================================ -->
      @if (allPlans().length > 0) {
        <section class="mb-8" aria-labelledby="mp-plans-title">
          <div class="mp-plans__head">
            <div>
              <h2 id="mp-plans-title" class="text-lg font-semibold text-gray-900 dark:text-white/90">Planes</h2>
              <p class="mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400">Elige según el tamaño de tu equipo; luego agrega solo lo que necesites.</p>
            </div>
            <div class="mp-cycle" role="radiogroup" aria-label="Frecuencia de cobro">
              <button type="button" role="radio" class="mp-cycle__item" [attr.aria-checked]="billingCycle() === 'monthly'"
                (click)="billingCycle.set('monthly')">Mensual</button>
              <button type="button" role="radio" class="mp-cycle__item" [attr.aria-checked]="billingCycle() === 'yearly'"
                (click)="billingCycle.set('yearly')">
                Anual <span class="mp-cycle__save">−15 %</span>
              </button>
            </div>
          </div>

          <div class="mp-grid">
            @for (p of sortedPlans(); track p.membershipPlanId) {
              @let selected = isSelectedPlan(p.membershipPlanId);
              @let current = isCurrentMembershipPlan(p.membershipPlanId);
              @let delta = planFeatureDelta(p);
              @let included = p.numberOfProfessionals || 1;
              <article class="mp-card" [class.mp-card--selected]="selected" (click)="selectPlan(p)">
                <div class="mp-card__tags">
                  @if (current) {
                    <span class="mp-tag mp-tag--current">Tu plan actual</span>
                  }
                  @if (isRecommendedPlan(p)) {
                    <span class="mp-tag">Recomendado</span>
                  }
                </div>

                <h3 class="mp-card__name">{{ p.name }}</h3>
                <p class="mp-card__desc">{{ p.description }}</p>

                <p class="mp-price">
                  <span class="mp-price__amount">{{ getPlanPrice(p) | currency:'COP':'symbol-narrow':'1.0-0' }}</span>
                  <span class="mp-price__unit">/ {{ billingCycle() === 'monthly' ? 'mes' : 'año' }}</span>
                </p>

                <div class="mp-team">
                  <svg class="size-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                  </svg>
                  <div>
                    <p class="mp-team__count">{{ included }} profesional{{ included > 1 ? 'es' : '' }} incluido{{ included > 1 ? 's' : '' }}</p>
                    @if (p.pricePerProfessional && p.pricePerProfessional > 0) {
                      <p class="mp-team__extra">Cada adicional, {{ p.pricePerProfessional | currency:'COP':'symbol-narrow':'1.0-0' }} al mes</p>
                    }
                  </div>
                </div>

                <div class="mp-features">
                  @if (delta.baseName) {
                    <p class="mp-features__lead">
                      Todo lo de {{ delta.baseName }}{{ delta.features.length ? ', y además:' : ', con más profesionales incluidos.' }}
                    </p>
                  }
                  <ul class="space-y-2">
                    @for (f of delta.features; track f.membershipPlanFeatureId) {
                      <li class="mp-feature">
                        <svg class="size-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                        </svg>
                        {{ f.featureName }}
                      </li>
                    }
                  </ul>
                </div>

                <button type="button" class="mp-card__cta" [attr.aria-pressed]="selected"
                  (click)="selectPlan(p); $event.stopPropagation()">
                  @if (selected) {
                    <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                    Seleccionado
                  } @else if (current) {
                    Volver a mi plan
                  } @else {
                    Elegir {{ p.name }}
                  }
                </button>
              </article>
            }
          </div>
        </section>

        <!-- Customize the selected plan and pay -->
        @if (selectedPlanDto(); as sp) {
          <section class="mp-custom mb-8" aria-labelledby="mp-custom-title">
            <div class="mp-custom__main">
              <h2 id="mp-custom-title" class="text-lg font-semibold text-gray-900 dark:text-white/90">Personaliza tu plan {{ sp.name }}</h2>
              <p class="mt-0.5 mb-4 text-theme-sm text-gray-500 dark:text-gray-400">Agrega solo lo que tu negocio necesita.</p>

              @if (additionalFeatures().length) {
                <ul class="mp-addons">
                  @for (feature of additionalFeatures(); track feature.membershipPlanFeatureId) {
                    @let on = isFeatureSelected(feature.membershipPlanFeatureId);
                    <li class="mp-addon" [class.mp-addon--on]="on">
                      <label class="mp-addon__row">
                        <input type="checkbox" class="mp-check" [checked]="on" (change)="toggleFeature(feature)" />
                        <span class="min-w-0 flex-1">
                          <span class="mp-addon__name">{{ feature.featureName }}</span>
                          @if (feature.featureDescription) {
                            <span class="mp-addon__desc">{{ feature.featureDescription }}</span>
                          }
                        </span>
                        <span class="mp-addon__price">
                          +{{ getFeaturePrice(feature) | currency:'COP':'symbol-narrow':'1.0-0' }}
                          <span>/ {{ billingCycle() === 'monthly' ? 'mes' : 'año' }}</span>
                        </span>
                      </label>

                      @if (feature.featureName === 'Múltiples profesionales' && on) {
                        <div class="mp-stepper">
                          <span class="text-theme-sm text-gray-600 dark:text-gray-300">
                            Profesionales en total
                            <span class="block text-xs text-gray-400">{{ currentPlan()?.numberOfProfessionals ?? 1 }} incluidos en el plan</span>
                          </span>
                          <div class="mp-stepper__control">
                            <button type="button" (click)="decrementProfessionals()" [disabled]="numberOfProfessionals() <= 0"
                              aria-label="Quitar un profesional">
                              <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M5 12h14"/></svg>
                            </button>
                            <span aria-live="polite">{{ getTotalProfessionals() }}</span>
                            <button type="button" (click)="incrementProfessionals()" aria-label="Agregar un profesional">
                              <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
                            </button>
                          </div>
                          @if (numberOfProfessionals() > 0) {
                            <span class="text-theme-sm font-medium text-gray-800 tabular-nums dark:text-white/90">
                              +{{ getProfessionalsCost() | currency:'COP':'symbol-narrow':'1.0-0' }}
                            </span>
                          }
                        </div>
                      }
                    </li>
                  }
                </ul>
              } @else {
                <p class="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-theme-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  Este plan ya incluye todas las funciones.
                </p>
              }
            </div>

            <aside class="mp-summary" aria-label="Resumen del pago">
              <p class="text-theme-sm font-medium text-gray-500 dark:text-gray-400">Resumen</p>
              <dl class="mp-summary__lines">
                <div>
                  <dt>Plan {{ sp.name }}</dt>
                  <dd>{{ basePlanPrice() | currency:'COP':'symbol-narrow':'1.0-0' }}</dd>
                </div>
                @for (item of selectedFeaturesBreakdown(); track item.featureId) {
                  <div>
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.amount | currency:'COP':'symbol-narrow':'1.0-0' }}</dd>
                  </div>
                }
                @if (billingCycle() === 'yearly' && annualDiscount() > 0) {
                  <div class="mp-summary__discount">
                    <dt>Descuento anual</dt>
                    <dd>−{{ annualDiscount() | currency:'COP':'symbol-narrow':'1.0-0' }}</dd>
                  </div>
                }
              </dl>
              <div class="mp-summary__total">
                <span>Total {{ billingCycle() === 'monthly' ? 'mensual' : 'anual' }}</span>
                <strong>{{ displayPrice() | currency:'COP':'symbol-narrow':'1.0-0' }}</strong>
              </div>
              <p class="mp-summary__next">Próximo cobro el {{ nextChargeDate() | date:"d 'de' MMMM 'de' y" }}</p>

              @if (upgradeOffer(); as up) {
                <div class="mp-upsell">
                  <p>
                    Por {{ up.extraPerMonth | currency:'COP':'symbol-narrow':'1.0-0' }} más al mes, {{ up.plan.name }} te da {{ upgradeGains() }}@if (up.savings) {<span>: {{ up.savings | currency:'COP':'symbol-narrow':'1.0-0' }} menos que agregarlo por separado</span>}.
                  </p>
                  <button type="button" (click)="chooseUpgrade()">Cambiar a {{ up.plan.name }}</button>
                </div>
              }

              @if (authServicePublic.isImpersonating()) {
                <p class="mt-4 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-xs font-medium text-warning-700 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-300">
                  No puedes pagar mientras ves el negocio como administrador.
                </p>
              } @else {
                <button type="button" class="mp-btn mp-btn--primary mt-4 w-full" (click)="confirmPay()" [disabled]="isProcessingPayment()">
                  {{ isProcessingPayment() ? 'Abriendo el pago…' : 'Pagar ' + (displayPrice() | currency:'COP':'symbol-narrow':'1.0-0') }}
                </button>
              }
              <p class="mp-summary__trust">
                <svg class="size-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                Pago seguro con ePayco
              </p>
            </aside>
          </section>
        }
      }

      <!-- ============================================================ -->
      <!-- MOBILE FLOATING CTA BAR                                      -->
      <!-- ============================================================ -->
      <div
        class="fixed inset-x-0 bottom-0 lg:hidden p-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-t border-gray-200 dark:border-gray-800 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] z-50">
        <div class="mx-auto max-w-6xl flex items-center justify-between gap-3">
          <div>
            <div class="text-sm font-semibold text-gray-900 dark:text-white">{{ plan().name }}</div>
            <div class="text-lg font-bold text-gray-900 dark:text-white">
              {{ displayPrice() | currency:'COP':'symbol-narrow':'1.0-0' }}
              <span class="text-xs font-normal text-gray-500">/ {{ billingCycle() === 'monthly' ? 'mes' : 'año' }}</span>
            </div>
          </div>
          <button
            class="mp-btn mp-btn--primary"
            (click)="confirmPay()" [disabled]="isProcessingPayment() || authServicePublic.isImpersonating()">
            {{ isProcessingPayment() ? 'Abriendo el pago…' : 'Pagar ahora' }}
          </button>
        </div>
      </div>

    }

    <ng-template #paymentRow let-inv>
      <li class="ms-payment" [attr.data-status]="inv.status">
        <span class="ms-payment__dot" aria-hidden="true"></span>
        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-baseline gap-x-2">
            <span class="ms-payment__desc">{{ inv.description || 'Suscripción ' + (inv.planName || plan().name) }}</span>
            <span class="ms-payment__status">{{ statusLabel(inv.status) }}</span>
          </div>
          <p class="ms-payment__meta">
            {{ inv.createdAt | date:'d MMM yyyy' }}
            @if (inv.period?.start && inv.period?.end) {
              <span>, cubre {{ inv.period.start | date:'d MMM':'UTC' }} – {{ inv.period.end | date:'d MMM yyyy':'UTC' }}</span>
            }
            @if (inv.method) {
              <span>, {{ inv.method.brand | uppercase }} terminada en {{ inv.method.last4 }}</span>
            }
          </p>
          @if (inv.failureReason) {
            <p class="ms-payment__error">{{ inv.failureReason }}</p>
          }
        </div>
        <span class="ms-payment__amount">{{ inv.amount | currency:(inv.currency || 'COP'):'symbol-narrow':'1.0-0' }}</span>
        @if (inv.status !== 'failed') {
          <button type="button" class="ms-receipt" (click)="downloadReceipt(inv)"
            [attr.aria-label]="'Descargar recibo del ' + (inv.createdAt | date:'d MMM yyyy')" title="Descargar recibo">
            <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </button>
        } @else {
          <span class="ms-receipt ms-receipt--empty" aria-hidden="true"></span>
        }
      </li>
    </ng-template>

  </div>
</div>

<!-- Payment Options Modal -->
<app-payment-options-modal
  [(open)]="showPaymentOptionsModal"
  [currentMonthPrice]="currentMonthAdditionalPrice()"
  [currentMonthPlusNextPrice]="currentMonthPlusNextPrice()"
  [currentMonthBreakdown]="getCurrentMonthBreakdown()"
  [nextMembershipBreakdown]="getNextMembershipBreakdown()"
  [selectedFeatures]="getSelectedNewFeatures()"
  [billingCycle]="billingCycle()"
  (confirm)="proceedWithPayment($event)"
  (cancel)="showPaymentOptionsModal.set(false)">
</app-payment-options-modal>
